import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { Reveal } from "@/components/Reveal";
import { IMAGES, SITE } from "@/data/site";
import { MessageCircle } from "lucide-react";

const FILTERS = ["all", "industrial", "panels", "wiring", "energy"];

export default function Projects() {
  const { t } = useLang();
  const [filter, setFilter] = useState("all");
  const items = t.projectsPage.items.filter((p) => filter === "all" || p.cat === filter);

  return (
    <div>
      {/* Header banner */}
      <section className="relative bg-navy text-white overflow-hidden" data-testid="projects-header">
        <div className="absolute inset-0 grid-pattern-dark opacity-60" />
        <div className="absolute -top-24 -left-24 w-[420px] h-[420px] rounded-full bg-royal/50 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-24">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-sky-300 font-semibold">{t.projectsPage.eyebrow}</p>
            <h1 className="mt-3 font-display text-4xl sm:text-5xl font-extrabold tracking-tight" data-testid="projects-title">{t.projectsPage.title}</h1>
            <p className="mt-4 max-w-2xl text-base sm:text-lg text-slate-300 leading-relaxed">{t.projectsPage.subtitle}</p>
          </Reveal>
        </div>
      </section>

      <section className="py-16 sm:py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Filter tabs */}
          <div className="flex flex-wrap gap-2.5" data-testid="projects-filters">
            {FILTERS.map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                data-testid={`projects-tab-${f === "all" ? "all" : f}`}
                className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-all duration-200 active:scale-[0.98] ${
                  filter === f
                    ? "bg-electric text-white shadow-card-hover"
                    : "bg-slate-100 text-slate-600 hover:bg-blue-50 hover:text-electric"
                }`}
              >
                {t.projectsPage.filters[f]}
              </button>
            ))}
          </div>

          {/* Grid */}
          <motion.div layout className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="projects-grid">
            <AnimatePresence mode="popLayout">
              {items.map((p, i) => (
                <motion.article
                  layout
                  key={p.img + p.cat}
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.96 }}
                  transition={{ duration: 0.35, delay: i * 0.04 }}
                  className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-card hover:shadow-card-hover transition-shadow duration-300"
                >
                  <div className="overflow-hidden aspect-[4/3]">
                    <img
                      src={IMAGES[p.img]}
                      alt={p.title}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5">
                    <span className="inline-block rounded-full bg-blue-50 px-3 py-1 text-[11px] font-mono uppercase tracking-wider text-electric font-semibold">
                      {t.projectsPage.filters[p.cat]}
                    </span>
                    <h3 className="mt-3 font-display text-base font-semibold text-navy leading-snug" data-testid="project-card-details">
                      {p.title}
                    </h3>
                    <p className="mt-1.5 text-xs font-mono text-slate-400">{p.meta}</p>
                  </div>
                </motion.article>
              ))}
            </AnimatePresence>
          </motion.div>

          <p className="mt-8 text-xs text-slate-400 italic">{t.projectsPage.note}</p>

          <Reveal>
            <div className="mt-12 rounded-2xl bg-navy text-white p-8 sm:p-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
              <div>
                <h2 className="font-display text-xl sm:text-2xl font-bold">{t.finalCta.title}</h2>
                <p className="mt-2 text-sm text-slate-300">{t.finalCta.subtitle}</p>
              </div>
              <div className="flex gap-3 shrink-0">
                <a
                  href={SITE.whatsappHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="projects-whatsapp-cta"
                  className="inline-flex items-center gap-2 rounded-full bg-wa px-6 py-3 text-sm font-semibold hover:bg-[#1fb855] active:scale-[0.98] transition-all duration-200"
                >
                  <MessageCircle className="w-4 h-4" />
                  {t.finalCta.whatsapp}
                </a>
                <Link
                  to="/contact"
                  data-testid="projects-contact-cta"
                  className="inline-flex items-center gap-2 rounded-full border border-white/30 px-6 py-3 text-sm font-semibold hover:bg-white/10 active:scale-[0.98] transition-all duration-200"
                >
                  {t.nav.contact}
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
